"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { Users, X } from "lucide-react";
import Lock from "@mui/icons-material/Lock";

import { useStyles } from "./styles";
import { MODELS } from "../constants";

const MAX_FILE_SIZE = 10;
const MAX_IMAGES = 9;

interface ModeSelectProps {
  selectedModel: string;
  images: File[];
  setImages: React.Dispatch<React.SetStateAction<File[]>>;
  selectedModes: {
    isPublic: boolean;
    is18Plus: boolean;
    isTextImage: boolean;
  };
  setSelectedModes: React.Dispatch<
    React.SetStateAction<{
      isPublic: boolean;
      is18Plus: boolean;
      isTextImage: boolean;
    }>
  >;
  isSubscribed: boolean;
}

const ModeSelect: React.FC<ModeSelectProps> = ({
  selectedModel,
  images,
  setImages,
  selectedModes,
  setSelectedModes,
  isSubscribed,
}) => {
  const { classes, cx } = useStyles();
  const router = useRouter();
  const model = MODELS.find((m) => m.name === selectedModel) || MODELS[0];
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const uploadButtonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const uploadLocked = !isSubscribed;

  useEffect(() => {
    if (showTooltip && uploadButtonRef.current && tooltipRef.current) {
      const rect = uploadButtonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const top = rect.bottom + 12;
      const left = rect.left + rect.width / 2 - tooltipRect.width / 2;

      setTooltipPosition({
        top: Math.min(top, window.innerHeight - tooltipRect.height - 10),
        left: Math.max(
          10,
          Math.min(left, window.innerWidth - tooltipRect.width - 10)
        ),
      });
    }
  }, [showTooltip]);

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  const handleModeChange = (mode: keyof ModeSelectProps["selectedModes"]) => {
    setSelectedModes((prev) => ({
      ...prev,
      [mode]: !prev[mode],
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      if (files.length + images.length > MAX_IMAGES) {
        alert(`You can only select up to ${MAX_IMAGES} images.`);
        return;
      }

      const validFiles = files.filter(
        (file) => file.size <= MAX_FILE_SIZE * 1024 * 1024
      );
      if (validFiles.length !== files.length) {
        alert(`Some files exceed the maximum size of ${MAX_FILE_SIZE} MB.`);
        return;
      }

      setImages((prevImages) => [...prevImages, ...files]);
    }
  };

  const handleUploadClick = () => {
    if (uploadLocked) {
      setShowTooltip(true);
    }
  };

  const handleUpgrade = () => {
    router.push("/subscribe");
    setShowTooltip(false);
  };

  return (
    <div className={classes.container}>
      <div className={classes.optionButtonContainer}>
        <div
          ref={uploadButtonRef}
          className={cx(classes.optionButton, {
            [classes.disabled]:
              !model.imageEdits || images.length >= MAX_IMAGES || uploadLocked,
          })}
          onClick={handleUploadClick}
        >
          <div
            className={cx(classes.optionIcon, {
              [classes.selected]: images.length > 0,
            })}
          >
            {!uploadLocked && (
              <input
                type="file"
                className={classes.fileInput}
                id="imageUpload"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                multiple
                onChange={handleImageUpload}
                onClick={(e) => e.stopPropagation()}
                disabled={!model.imageEdits || images.length >= MAX_IMAGES}
              />
            )}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
          </div>
          <span className={classes.optionLabel}>Upload</span>
        </div>
        {uploadLocked && (
          <div className={classes.locked}>
            <Lock />
          </div>
        )}
      </div>
      <div
        className={classes.optionButton}
        onClick={() => handleModeChange("isPublic")}
      >
        <div
          className={cx(classes.optionIcon, {
            [classes.selected]: selectedModes.isPublic,
          })}
        >
          <Users />
        </div>
        <span className={classes.optionLabel}>Public</span>
      </div>
      <div
        className={cx(classes.optionButton, {
          [classes.disabled]: !model.nsfw,
        })}
        onClick={() => model.nsfw && handleModeChange("is18Plus")}
      >
        <div
          className={cx(classes.optionIcon, {
            [classes.selected]: selectedModes.is18Plus,
          })}
        >
          <span className={classes.optionText}>18+</span>
        </div>
        <span className={classes.optionLabel}>18+</span>
      </div>
      <div
        className={cx(classes.optionButton, {
          [classes.disabled]: !model.imageText,
        })}
        onClick={() => model.imageText && handleModeChange("isTextImage")}
      >
        <div
          className={cx(classes.optionIcon, {
            [classes.selected]: selectedModes.isTextImage,
          })}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="4" y1="6" x2="20" y2="6"></line>
            <line x1="4" y1="12" x2="20" y2="12"></line>
            <line x1="4" y1="18" x2="14" y2="18"></line>
          </svg>
        </div>
        <span className={classes.optionLabel}>Text</span>
      </div>
      {showTooltip && (
        <div
          ref={tooltipRef}
          className={classes.tooltip}
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <button
            className={classes.tooltipClose}
            onClick={() => setShowTooltip(false)}
          >
            <X size={14} />
          </button>
          <p className={classes.tooltipMessage}>To upload images</p>
          <button className={classes.tooltipUpgrade} onClick={handleUpgrade}>
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
};

export default ModeSelect;
